import { useCallback, useEffect, useState } from 'react';
import { getEventRoundView, getHighJumpRoundView } from 'api/resultApi';

export const useEventRoundData = (params, isHighJump) => {
  const [state, setState] = useState({ data: null, loading: true, error: null });

  useEffect(() => {
    let cancelled = false;
    // Keep prior data visible while refetching — avoids full-page flash on
    // tab switches. Only loading=true is signalled.
    setState((prev) => ({ data: prev.data, loading: true, error: null }));

    const fetcher = isHighJump ? getHighJumpRoundView : getEventRoundView;
    fetcher(params)
      .then((data) => {
        if (cancelled) return;
        setState({ data, loading: false, error: null });
      })
      .catch((error) => {
        if (cancelled) return;
        setState({ data: null, loading: false, error });
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.clubID, params.meetID, params.eventID, isHighJump]);

  // Fetches the same event again, and resolves once the fresh data is in
  // state. Used after results are saved or deleted, so the page and the
  // results dialog show what was stored without reloading the whole page.
  // One request; the page's other data (meet, club records) is left alone.
  const refetch = useCallback(async () => {
    const fetcher = isHighJump ? getHighJumpRoundView : getEventRoundView;
    try {
      const data = await fetcher({
        clubID: params.clubID,
        meetID: params.meetID,
        eventID: params.eventID,
      });
      setState({ data, loading: false, error: null });
    } catch (error) {
      setState({ data: null, loading: false, error });
    }
  }, [params.clubID, params.meetID, params.eventID, isHighJump]);

  // Changes one result in place with what the server answered for it (a relay
  // team's runners): no request.
  const updateResult = useCallback((resultID, changes) => {
    setState((prev) =>
      prev.data
        ? {
            ...prev,
            data: {
              ...prev.data,
              results: (prev.data.results || []).map((result) =>
                result.resultID === resultID ? { ...result, ...changes } : result
              ),
            },
          }
        : prev
    );
  }, []);

  return { ...state, refetch, updateResult };
};
